import React, { useCallback, useMemo, useRef } from 'react'
import shallow from 'zustand/shallow'
import { makeStyles } from '@material-ui/core/styles'
import SedaTimeseriesBase from './SedaTimeseriesBase'
import clsx from 'clsx'
import {
	useAppContext,
	useUiStore
} from '../../app/hooks'
import { getLang } from '../../app/selectors/lang'
import {
	getLocatonIdFromEvent,
	getCoordsFromEvent,
	isMarkerRelated
} from '../utils'
import { TIMESERIES_X_EXTENTS, TIMESERIES_Y_EXTENTS, TIMESERIES_X_EXTENTS_MISSING } from '../constants'
import useResizeAware from '../../../../shared/hooks/useResizeAware'

const useStyles = makeStyles(theme => ({
	root: {
		position: 'relative',
		width: '100%',
		paddingBottom: '75%',
		userSelect: 'none'
	},
	chart: {
		height: '100%',
		width: `calc(100% - ${theme.spacing(6)}px) !important`,
		position: 'absolute',
		left: theme.spacing(4),
		'& .timeseries-axis .MuiTypography-root.MuiTypography-body2': {
			fontSize: theme.typography.pxToRem(14)
		}
	},
	// make space for toggle button
	headerOffset: {
		[theme.breakpoints.up('sm')]: {
			right: 88
		}
	},
	toggleButton: {
		marginLeft: 0,
		minWidth: 80,
		maxWidth: 88,
		whiteSpace: 'normal',
		fontSize: theme.typography.pxToRem(12),
		position: 'absolute',
		top: theme.spacing(-8),
		right: -32,
		[theme.breakpoints.up('md')]: {
			position: 'absolute',
			top: -1,
			right: -88
		}
	}
}))

const SedaTimeseries = ({
	locationData
}) => {
	// ref to track the timeout that clears the tooltip
	const timeoutRef = useRef(null)

	// track size of chart area
	const [resizeListener] = useResizeAware()

	const [setHovered] = useUiStore(
		state => [
			state.setHovered,
		],
		shallow
	)
	// pull required items from the app context
	const {
		region,
		demographic,
		isVersus,
		colorExtent,
	} = useAppContext()
	// let [metric] = useMetric()
	const metric = "avg";
	// const [unit, setUnit] = useUnit()
	const xExtents = TIMESERIES_X_EXTENTS
	const xExtentsMissing = TIMESERIES_X_EXTENTS_MISSING
	const timeseriesVars = ['x', 'y']
	const [, yVar] = timeseriesVars
	// const defaultUnit = getMetricUnitDefault(metric)
	const xRange = [...Array(xExtents[1] - xExtents[0] + 1).keys()].map(x => x + xExtents[0])
	
	const timeseriesData = useMemo(() =>
		xRange.map(x => {
			if(!locationData) return
			const { id } = locationData
			let y = locationData[`${demographic}_avg${x}`]
			// if(typeof y === 'number') y = y.toFixed(3)
			if(y === undefined) return null
			return { x, y, id }
		}).filter(d => d)
	, [locationData, xRange, demographic, metric])

	const trendDataParams = [
		{
			id: 'before-covid-trend',
			suffix: '0919',
			range: [xExtents[0], xExtentsMissing[0]]
		},
		{
			id: 'after-covid-trend',
			suffix: '2225',
			range: [xExtentsMissing[1], xExtents[1]]
		}
	];

	const timeseriesTrendData = trendDataParams.map(({ id, suffix, range }) => ({
		id,
		data: range.map(x => {
			if(!locationData) return
			const { id } = locationData
			const y = locationData[`${demographic}_avg${x}`]
			? locationData[`${demographic}_avg_${suffix}`] + locationData[`${demographic}_coh_${suffix}`] * (x - ((range[0] + range[1]) / 2))
			: null
			return { x, y, id }
		})
	}))

	const yVals = useMemo(() =>
		timeseriesData.map(d => d[yVar]).filter(d => !Number.isNaN(d))
	, [timeseriesData])
	const yMin = useMemo(() =>
		Math.floor(Math.min(...yVals))
	, [yVals])
	const yMax = useMemo(() =>
		Math.ceil(Math.max(...yVals))
	, [yVals])
	// Use default min/max unless data exceeds
	const yExtents = useMemo(() =>
		[
			// yMin < TIMESERIES_Y_EXTENTS[0] ? yMin : TIMESERIES_Y_EXTENTS[0],
			// yMax > TIMESERIES_Y_EXTENTS[1] ? yMax : TIMESERIES_Y_EXTENTS[1],
			yMin < TIMESERIES_Y_EXTENTS[metric][0] ? yMin : TIMESERIES_Y_EXTENTS[metric][0],
			yMax > TIMESERIES_Y_EXTENTS[metric][1] ? yMax : TIMESERIES_Y_EXTENTS[metric][1],
		]
	, [yMin, yMax, metric])
	// const yExtents = Y_EXTENTS[metric]
	const timeseriesExtents = [xExtents, yExtents]
	const yAxisLabel = getLang('TIMESERIES_AXIS_'+metric)
	// handle hover events
	const handleHover = useCallback(
		(e, eChartInstance, vars) => {
			// grab data from event
			const id = getLocatonIdFromEvent(e)
			const coords = getCoordsFromEvent(e)
			if (e.type === 'mouseover') {
				// set the current hovered ID and coords
				setHovered(id, coords, {
					showTooltip: true,
					showMarkers: true,
					forceVars: [vars[1], vars[0]] // y and x vars from chart
				})
				// if the tooltip is going to be cleared, cancel it
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current)
					timeoutRef.current = null
				}
			}
			// set a timeout to clear the tooltip
			if (e.type === 'mouseout' && !isMarkerRelated(e)) {
				if (timeoutRef.current) clearTimeout(timeoutRef.current)
				timeoutRef.current = setTimeout(
					() => setHovered(null),
					400
				)
			}
		},
		[setHovered]
	)

	const classes = useStyles()

	return (
		<div className={clsx(classes.root)}>
			{resizeListener}
			<SedaTimeseriesBase
				className={clsx(classes.chart, {
					'timeseries--versus': isVersus
				})}
				variant="map"
				data={timeseriesData}
				trendData={timeseriesTrendData}
				vars={timeseriesVars}
				region={region}
				metric={metric}
				extents={timeseriesExtents}
				colorExtent={colorExtent}
				xExtentsMissing={xExtentsMissing}
				yAxisLabel={yAxisLabel}
				onHover={handleHover}
			>
			</SedaTimeseriesBase>
		</div>
	)
}

export default SedaTimeseries
